import { NetworkStatus, useQuery } from '@apollo/client';
import { getDraftOrdersTemplate } from 'apollo/templates/order';
import DraftOrderList from 'components/orders/draft-order-list/draft-order-list.component';
import DraftOrdersPageActions from 'components/orders/draft-order-list/draft-orders-page-actions.component';
import Loader from 'components/shared/loader/loader.component';
import { DraftOrderFilter, Order, OrderScope } from 'models/order';
import React, { FC, Fragment, useState } from 'react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const DraftOrders: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const [lastOffset, setLastOffset] = useState(0);
    const [draftOrders, setDraftOrders] = useState<Order[]>([]);
    const [draftFilters, setDraftFilters] = useState<DraftOrderFilter>({ scope: OrderScope.MY_DRAFTS });
    const { showBoundary } = useDenthubErrorBoundary();

    const { loading, fetchMore, networkStatus } = useQuery(getDraftOrdersTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { limit: 20, offset: 0, filters: draftFilters },
        onCompleted: ({ draftOrders }) => {
            setDraftOrders(draftOrders);
            setLastOffset(draftOrders.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFilterChange = (filters: DraftOrderFilter) => {
        setDraftFilters(filters);
        setLastOffset(0);
    };

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    const removeOrder = (orderId: string) => {
        const orders = JSON.parse(JSON.stringify(draftOrders));
        const index = orders.findIndex((order: Order) => order._id === orderId);
        if (index !== -1) {
            orders.splice(index, 1);
            setDraftOrders(orders);
        }
    };

    return (
        <Fragment>
            <Helmet>
                <title>{translate('orders:DRAFT_ORDERS')} | Denthub</title>
            </Helmet>
            <DraftOrdersPageActions onFilterChange={handleFilterChange} />
            {loading && networkStatus !== NetworkStatus.fetchMore ? (
                <Loader />
            ) : (
                <DraftOrderList draftOrders={draftOrders} onFetchMore={handleFetchMore} removeOrder={removeOrder} />
            )}
        </Fragment>
    );
};

export default DraftOrders;
